import * as React from "react";
import {isRequestError} from "../../../Model/RequestError";
import dropin, {Dropin} from "braintree-web-drop-in";
import {Address} from "../../../Model/Sales/Address";
import {searchPostcode} from "../../../Api/Sales/PostcodeSearchApi";
import {setInputValue} from "../../../Utilities/FormUtilities";
import {CmsContent} from "../../../Model/CmsContent/CmsContent";
import {ApplicationContext} from "../../../ApplicationContext";
import {useNavigate} from "react-router-dom";
import {SALES_PORTAL_ROUTES} from "../../../Routes/SalesPortalRoutes";
import {createBraintreeClientToken} from "../../../Api/Sales/BraintreeApi";
import {isNonEmptyString} from "../../../Utilities/ValidationUtilities";
import {CmsFormSchema} from "../../../Model/CmsContent/CmsFormSchema";
import {PATTERN_POSTCODE} from "../../../Constant/ValidationRegex";
import {applyInputError, toggleInputError} from "../../../Utilities/CmsFormUtilities";
import {ReactNode, useRef} from "react";
import BraintreeDropin from "./BraintreeDropIn";
import DateOfBirthInput from "./DateOfBirthInput";

interface SimOnlyPlanOnePagePurchaseFormProps {
    readonly formId: string;
    readonly cmsContent: CmsContent[];
    readonly submitForm: (formData: FormData) => {};
    readonly setBraintreeInstance: (dropIn: Dropin) => void;
    readonly braintreeInstance: Dropin | undefined;
    readonly paymentPurchaseSummary: ReactNode;
    readonly isSimInHandJourney: boolean;
    readonly amountToPay: number | null;
}

const SimOnlyPlanOnePagePurchaseForm = (props: SimOnlyPlanOnePagePurchaseFormProps): JSX.Element | null => {

    const [braintreeClientToken, setBraintreeClientToken] = React.useState<string | null>(null)

    const [email, setEmail] = React.useState("");
    const [confirmEmail, setConfirmEmail] = React.useState("");
    const [confirmTouched, setConfirmTouched] = React.useState(false);

    React.useEffect (() => {
        if (confirmTouched) {
            if (confirmEmail && confirmEmail !== email) {
                applyInputError([], props.formId, "email-address-confirmation", "The emails do not match")
            } else {
                toggleInputError([], props.formId, "email-address-confirmation", undefined)
            }
        }
    }, [email, confirmEmail, confirmTouched]);

    const applicationContext = React.useContext(ApplicationContext)
    const navigate = useNavigate()

    const setUpAddressLookUp = (lookUpButtonId: string,
                                postcodeId: string,
                                setFoundAddresses: React.Dispatch<React.SetStateAction<Address[]>>
    ) => {
        const btn = document.getElementById(lookUpButtonId);

        if (!btn) return;

        const handleClick = () => {
            searchAddresses(postcodeId, setFoundAddresses);
        };

        const searchAddresses = (postcodeId: string, setFoundAddresses: React.Dispatch<React.SetStateAction<Address[]>>): void => {

            const form = document.getElementById(props.formId) as HTMLFormElement | null;
            if (!form) return undefined;

            const formData = new FormData(form);
            const formCmsContent = props.cmsContent.find((content) => content.reference === props.formId)
            if (!formCmsContent) {
                return
            }
            const cmsForm = formCmsContent.jsonContent as CmsFormSchema
            const errorMap = cmsForm.errorMap
            const postCode = formData.get(postcodeId)
            const postCodeNotBlank = isNonEmptyString(postCode)

            const postCodeValid = isNonEmptyString(postCode) && PATTERN_POSTCODE.test(postCode)
            const postcodeValid = postCodeNotBlank && postCodeValid
            applyInputError(errorMap, props.formId, postcodeId, postCodeNotBlank ? undefined : "A postCode was expected but was not provided")
            applyInputError(errorMap, props.formId, postcodeId, postCodeValid ? undefined : "The given postCode was not valid")

            if (postcodeValid) {
                searchPostcode(applicationContext.salesPortalApiDomain, applicationContext.reseller, postCode as string).then((maybeAddresses) => {
                    if (isRequestError(maybeAddresses)) {
                        applyInputError(errorMap, props.formId, postcodeId, "No addresses with the given post code could be found")
                    } else {
                        setFoundAddresses(maybeAddresses)
                        toggleInputError(errorMap, props.formId, postcodeId, undefined)
                    }
                })
            }
        }


        btn.addEventListener("click", handleClick)

        return () => {
            btn.removeEventListener("click", handleClick);
        };
    }

    const populateFormWithSelectedAddress = (idPrefix: string, selectedAddress: Address | null) => {
        if (selectedAddress) {
            setInputValue(`${idPrefix}Line1`, selectedAddress.line1)
            setInputValue(`${idPrefix}Line2`, selectedAddress.line2)
            setInputValue(`${idPrefix}City`, selectedAddress.town)
            setInputValue(`${idPrefix}Postcode`, `${selectedAddress.postcode1} ${selectedAddress.postcode2}`)
        }
    }

    const createAddressPicker = (foundAddresses: Address[], idPrefix: string) => {
        if (foundAddresses.length < 1) {
            return
        }

        const select = document.getElementById(
            `${idPrefix}Addresses`
        )

        if (!(select instanceof HTMLSelectElement)) return

        select.innerHTML = ""

        const option = document.createElement("option")
        option.value = "0"
        option.disabled = true
        option.textContent = "Please select an address"
        select.appendChild(option)

        foundAddresses.forEach((address, index) => {
            const option = document.createElement("option")
            option.value = (index + 1).toString()
            option.textContent = address.addressSummary
            select.appendChild(option)
        })

        select.value = "0"
        select?.parentElement?.removeAttribute("hidden")
    }


    const setUpUseAsShippingAddressToggle = () => {

        const checkbox = document.getElementById(`useBillingAddressAsDelivery`) as HTMLInputElement | null;

        if (!checkbox) return;

        const handleChange = (e: Event) => {
            const target = e.target as HTMLInputElement;
            const deliveryAddressSection = document.getElementById(`deliveryAddressSection`) as HTMLElement;
            if (target.checked) {
                if (deliveryAddressSection) {
                    deliveryAddressSection.setAttribute("hidden", "true")
                }
            } else {
                if (deliveryAddressSection) {
                    deliveryAddressSection.removeAttribute("hidden")
                }
            }
        };

        checkbox.addEventListener("change", handleChange);

        return () => {
            checkbox.removeEventListener("change", handleChange);
        };
    }

    React.useEffect(() => {
        let cleanups: Array<() => void> = [];

        const checkboxCleanUp = setUpUseAsShippingAddressToggle()
        if (checkboxCleanUp) {cleanups.push(checkboxCleanUp);}


        return () => {
            cleanups.forEach(fn => fn());
        };
    }, []);


    React.useEffect(() => {
        const setup = async () => {
            await initializeBraintree();
        };
        setup();
    }, [])

    const initializeBraintree = async () => {
        if (props.amountToPay === undefined) return;
        const maybeClientToken = await createBraintreeClientToken(applicationContext.salesPortalApiDomain);

        if (isRequestError(maybeClientToken)) {
            navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext));
            return;
        }
        setBraintreeClientToken(maybeClientToken.clientToken)
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!props.braintreeInstance) {
            return;
        }

        if (!formRef.current) {
            console.error("Form ref not found");
            return;
        }
        const formData = new FormData(formRef.current);

        props.submitForm(formData);

    };

    const formRef = useRef<HTMLFormElement>(null);

    return (
        <>
            <style>
                {`
                .dob-group {
                  display: flex;
                  gap:30px !important;
                }
                
                .dob-group input {
                  padding-top: 8px;
                  padding-bottom: 8px;
                  font-size: 16px;
                  text-align: center;
                }
                
                .dob-group input:nth-child(1),
                .dob-group input:nth-child(3) {
                  width: 40px;
                }
                
                .dob-group input:nth-child(5) {
                  width: 60px;
                }
                `}
            </style>
           <div>
                <h3 className="sp-text-primary">{props.isSimInHandJourney ? "" : "Buy a Plan"}</h3>

               <form
                   id={props.formId}
                   ref={formRef}
                   noValidate
                   onSubmit={handleSubmit}
               >
                   {/* Email Address */}
                   <div className="form-group ">
                       <label htmlFor="email-address" className="sp-text-primary subtitle-label">Email Address</label>
                       <input required id="email-address" name="email-address" placeholder="Type your email address*"
                              onPaste={(e) => e.preventDefault()} type="email" autoComplete="nope"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}/>
                   </div>
                   <div
                       id={`${props.formId}:email-address-error-wrapper`}
                       className="sp-form-field-error-wrapper"
                       style={{display: "none"}}>

                       <div id={`${props.formId}:email-address-error`} className="sp-form-text-error"></div>
                   </div>

                   {/* Confirm Email */}
                   <div className="form-group ">
                       <label htmlFor="email-address-confirmation" className="sp-text-primary subtitle-label">Confirm
                           Email</label>
                       <input id="email-address-confirmation" name="email-address-confirmation"
                              placeholder="Retype your email address*"
                              onPaste={(e) => e.preventDefault()}
                              type="email"
                              autoComplete="nope"
                              value={confirmEmail}
                              onChange={(e) => setConfirmEmail(e.target.value)}
                              onBlur={() => setConfirmTouched(true)}/>
                       <div
                           id={`${props.formId}:email-address-confirmation-error-wrapper`}
                           className="sp-form-field-error-wrapper"
                           style={{display: "none"}}>

                           <div id={`${props.formId}:email-address-confirmation-error`}
                                className="sp-form-text-error"></div>
                       </div>
                   </div>


                   {/* Billing Address Section */}
                   <div>
                       <p className="sp-text-primary subtitle-label">Billing Address</p>

                   </div>

                   {/* First Name */}
                   <div className="form-row">
                       <div className="form-group">

                           <input type="text" id="forename" name="forename"
                                  placeholder="First Name*"/>
                           <label htmlFor="forename" className="sr-only">First Name</label>
                           <div
                               id={`${props.formId}:forename-error-wrapper`}
                               className="sp-form-field-error-wrapper"
                               style={{display: "none"}}>

                               <div id={`${props.formId}:forename-error`}
                                    className="sp-form-text-error"></div>
                           </div>
                       </div>

                       {/* Last Name */}
                       <div className="form-group">

                           <input type="text" id="surname" name="surname"
                                  placeholder="Last Name*"/>
                           <label htmlFor="surname" className="sr-only">Last Name</label>
                           <div
                               id={`${props.formId}:surname-error-wrapper`}
                               className="sp-form-field-error-wrapper"
                               style={{display: "none"}}>

                               <div id={`${props.formId}:surname-error`}
                                    className="sp-form-text-error"></div>
                           </div>
                       </div>
                   </div>

                   {/* Billing UK Postcode */}
                   <div className="form-row" style={{display: "flex"}}>
                       <div className="form-group" style={{flex: 2}}>
                           <input
                               type="text"
                               id="billingPostcode"
                               name="billingPostcode"
                               placeholder="Postcode*"

                           />
                           <label htmlFor="billingPostcode" className="sr-only">
                               UK Postcode
                           </label>
                           <div
                               id={`${props.formId}:billingPostcode-error-wrapper`}
                               className="sp-form-field-error-wrapper"
                               style={{display: "none"}}>

                               <div id={`${props.formId}:billingPostcode-error`}
                                    className="sp-form-text-error"></div>
                           </div>

                       </div>
                   </div>

                   {/* Billing Address Lines */}
                   <div className="form-group">
                       <label htmlFor="billingLine1" className="sr-only">
                           Line 1
                       </label>
                       <input
                           type="text"
                           id="billingLine1"
                           name="billingLine1"
                           placeholder="Line 1*"
                       />
                       <div
                           id={`${props.formId}:billingLine1-error-wrapper`}
                           className="sp-form-field-error-wrapper"
                           style={{display: "none"}}>

                           <div id={`${props.formId}:billingLine1-error`}
                                className="sp-form-text-error"></div>
                       </div>
                   </div>

                   <div className="form-group ">
                       <label htmlFor="billingLine2" className="sr-only">Line 2 (Optional)</label>
                       <input type="text" id="billingLine2" name="billingLine2"
                              placeholder="Line 2"/>
                       <div
                           id={`${props.formId}:billingLine2-error-wrapper`}
                           className="sp-form-field-error-wrapper"
                           style={{display: "none"}}>

                           <div id={`${props.formId}:billingLine2-error`}
                                className="sp-form-text-error"></div>
                       </div>
                   </div>

                   <div className="form-group ">
                       <input
                           type="text"
                           id="billingCity"
                           name="billingCity"
                           placeholder="Town/City*"
                       />
                       <label htmlFor="billingCity" className="sr-only">Town/City</label>
                       <div
                           id={`${props.formId}:billingCity-error-wrapper`}
                           className="sp-form-field-error-wrapper"
                           style={{display: "none"}}>

                           <div id={`${props.formId}:billingCity-error`}
                                className="sp-form-text-error"></div>
                       </div>
                   </div>

                   {/* Use as Shipping Address */}

                   {!props.isSimInHandJourney && <>
                       <div className="form-row">
                           <div className="sp-form-field-wrapper-checkbox">
                               <input className="margin-left-2 checkbox"
                                      type="checkbox"
                                      id="useBillingAddressAsDelivery"
                                      name="useBillingAddressAsDelivery"
                               />
                           </div>
                           <div className="sp-form-field-tooltip-wrapper">
                               <label htmlFor="useBillingAddressAsDelivery">Use as shipping address</label>
                           </div>
                       </div>

                       {/* Delivery Address */}
                       <div id="deliveryAddressSection">
                           <div>
                               <p className="sp-text-primary subtitle-label">Delivery Address</p>
                           </div>

                           {/* Delivery UK Postcode */}
                           <div className="form-row" style={{display: "flex"}}>

                               <div className="form-group" style={{flex: 2}}>
                                   <label htmlFor="deliveryPostcode" className="sr-only">
                                       UK Postcode <span title="Enter a valid UK postcode">ⓘ</span>
                                   </label>
                                   <input
                                       type="text"
                                       id="deliveryPostcode"
                                       name="deliveryPostcode"
                                       placeholder="Postcode*"
                                   />
                                   <div
                                       id={`${props.formId}:deliveryPostcode-error-wrapper`}
                                       className="sp-form-field-error-wrapper"
                                       style={{display: "none"}}>

                                       <div id={`${props.formId}:deliveryPostcode-error`}
                                            className="sp-form-text-error"></div>
                                   </div>
                               </div>
                           </div>

                           {/* Delivery Address Lines */}
                           <div className="form-group">
                               <label htmlFor="deliveryLine1" className="sr-only">Line 1</label>
                               <input
                                   type="text"
                                   id="deliveryLine1"
                                   name="deliveryLine1"
                                   placeholder="Line 1*"
                               />
                               <div
                                   id={`${props.formId}:deliveryLine1-error-wrapper`}
                                   className="sp-form-field-error-wrapper"
                                   style={{display: "none"}}>

                                   <div id={`${props.formId}:deliveryLine1-error`}
                                        className="sp-form-text-error"></div>
                               </div>
                           </div>

                           <div className="form-group">
                               <label htmlFor="deliveryLine2" className="sr-only">Line 2 (Optional)</label>
                               <input type="text" id="deliveryLine2" name="deliveryLine2" placeholder="Line 2"/>
                               <div
                                   id={`${props.formId}:deliveryLine2-error-wrapper`}
                                   className="sp-form-field-error-wrapper"
                                   style={{display: "none"}}>

                                   <div id={`${props.formId}:deliveryLine2-error`}
                                        className="sp-form-text-error"></div>
                               </div>
                           </div>

                           <div className="form-group">
                               <label htmlFor="deliveryCity" className="sr-only">Town/City</label>
                               <input
                                   type="text"
                                   id="deliveryCity"
                                   name="deliveryCity"
                                   placeholder="Town/City*"
                               />
                               <div
                                   id={`${props.formId}:deliveryCity-error-wrapper`}
                                   className="sp-form-field-error-wrapper"
                                   style={{display: "none"}}>

                                   <div id={`${props.formId}:deliveryCity-error`}
                                        className="sp-form-text-error"></div>
                               </div>
                           </div>
                       </div>
                   </>
                   }

                   {/* Date of Birth */}
                   <div>
                       <p className="sp-text-primary subtitle-label">Date of Birth*</p>
                   </div>
                   <div className="dob-group form-group">
                       <label htmlFor="dob" className="sr-only">
                           Date of Birth
                       </label>
                       <div>

                           <DateOfBirthInput/>
                       </div>

                   </div>
                   <div
                       id={`${props.formId}:dateOfBirth-error-wrapper`}
                       className="sp-form-field-error-wrapper"
                       style={{display: "none"}}>

                       <div id={`${props.formId}:dateOfBirth-error`}
                            className="sp-form-text-error"></div>
                   </div>
                   <div
                       id={`${props.formId}:terms-error-wrapper`}
                       className="sp-form-field-error-wrapper"
                       style={{display: "none"}}>

                       <div id={`${props.formId}:terms-error`}
                            className="sp-form-text-error"></div>
                   </div>
                   {/* Preferences */}
                   <div>
                       <p className="sp-text-primary subtitle-label">Staying in touch</p>
                       <p>
                           To help you get the most value from your Superdrug Mobile plan, we may share updates and
                           offers in accordance with our {" "}
                           <a href="https://www.superdrug.com/acc/privacypolicy"
                              target="_blank" rel="noreferrer">
                               privacy policy
                           </a>. If you don’t want to receive these,
                           simply tick the boxes below. You can change your preferences anytime
                           through your account settings or by getting in touch.
                       </p>
                       <p><b>I do not wish to receive offers by:</b></p>
                   </div>

                   <div className="form-row">
                       <div className="sp-form-field-wrapper-checkbox">
                           <input className="margin-left-2 checkbox" type="checkbox" id="smsMarketing"
                                  name="smsMarketing"/>
                       </div>
                       <div className="sp-form-field-tooltip-wrapper">
                           <label htmlFor="smsMarketing">SMS</label>
                       </div>
                   </div>

                   <div className="form-row">
                       <div className="sp-form-field-wrapper-checkbox">
                           <input className="margin-left-2 checkbox" type="checkbox" id="emailMarketing"
                                  name="emailMarketing"/>
                       </div>
                       <div className="sp-form-field-tooltip-wrapper">
                           <label htmlFor="emailMarketing">Email</label>
                       </div>

                   </div>

                   <div className="form-row">
                       <div className="sp-form-field-wrapper-checkbox">
                           <input className="margin-left-2 checkbox" type="checkbox" id="postMarketing"
                                  name="postMarketing"/>
                       </div>
                       <div className="sp-form-field-tooltip-wrapper">
                           <label htmlFor="postMarketing">Post</label>
                       </div>
                   </div>

                   {props.paymentPurchaseSummary}
                   <div>
                       <div><p className="sp-text-primary subtitle-label">Payment Details</p></div>

                       {braintreeClientToken && <BraintreeDropin
                           amount={props.amountToPay !== null ? props.amountToPay.toFixed(2) : null}
                           clientToken={braintreeClientToken}
                           setBraintreeInstance={props.setBraintreeInstance}/>}

                       <p className="margin-top-5">
                           By providing your card information you give
                           Superdrug Mobile permission to charge your
                           card for future payments, which will
                           automatically renew your plan. You can
                           change this any time through your Superdrug
                           Mobile dashboard.
                       </p>
                   </div>

                   <button type="submit"
                           style={{textTransform: 'none'}}
                           className="sp-link-button-blue sp-button sp-link-button place-order-button"
                   >
                       Place Order
                   </button>
               </form>
           </div>
        </>
    );
}

export default SimOnlyPlanOnePagePurchaseForm